import { collection, doc, getDoc, getDocs, orderBy, query, where } from "firebase/firestore";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useUserContext } from "../../context/UserProvider";
import { db } from "../../firebase";
import Spinner from "../util/Spinner";
import Onboarding from "./Onboarding";
import {
  BibCommunityCard,
  BodyWeightProgramBlock,
  ConfirmationLayout,
  ConfirmationSectionTitle,
  DashboardCtaCard,
  DeviceTrackingCard,
  getRegistrationSubtitle,
  getConfirmationTitle,
  getDashboardId,
  getDisplayRaceName,
  GiftProductsCard,
  HEALTH_AND_FITNESS_BUNDLE_LINKS,
  RaceReadyContext,
  ReferralCard,
  ResourceLinksCard,
  StrengthProgramBlock,
} from "./ConfirmationUI";
import { SendAnalyticsEvent, SendPageView, SendRegistration } from "../../func/analyticsFunctions";
import { isInviteVisit } from "./RaceReady/inviteLink";
import RaceReadyChecklist from "./RaceReady/RaceReadyChecklist";
import { getWelcomeTheme } from "./RaceReady/welcomeThemes";
import WelcomeInvite from "./RaceReady/WelcomeInvite";

const COMMUNITY_HOME_URL = "https://race.mychallengecanada.ca";

function withCommunityHome(raceData) {
  if (!raceData) return raceData;
  return {
    ...raceData,
    metadata: {
      ...raceData.metadata,
      fb_link: COMMUNITY_HOME_URL,
    },
  };
}

// Same lookup order as the dashboard's picture tool.
function getImagePreset(selectedRace, raceData, legDetails) {
  return (
    selectedRace?.raceInfo?.metadata?.imagePreset ||
    selectedRace?.imagePreset ||
    raceData?.metadata?.imagePreset ||
    raceData?.imagePreset ||
    legDetails?.metadata?.imagePreset ||
    legDetails?.imagePreset ||
    ""
  );
}

function isPreviewOrigin(origin = "") {
  return origin === "https://mcc-staging-4525c.web.app" || origin === "http://localhost:3000";
}

function buildPreviewFallback(raceName, paramValue) {
  const previewNames = {
    chasing2026: "Chasing Feathers Challenge 2026",
    chasing2026s: "Chasing Feathers Challenge 2026",
    bloom2026: "Bloom Challenge 2026",
    bloom2026s: "Bloom Challenge 2026",
    lakes2026: "Canada Lakes Run 2026",
    lakes2026s: "Canada Lakes Run 2026",
    nature2026: "Nature Calls Challenge 2026",
    nature2026s: "Nature Calls Challenge 2026",
    march2026: "The Slush and Sweat Challenge 2026",
    march2026s: "The Slush and Sweat Challenge 2026",
    february26: "It's Still Winter - February Challenge 2026",
    february26s: "It's Still Winter - February Challenge 2026",
    "60northrun26": "60° North Run 2026",
    spark2026: "The Social Spark Challenge 2026",
    spark2026s: "The Social Spark Challenge 2026",
    fall2026: "Joy of Fall Run 2026",
    fall2026s: "Joy of Fall Run 2026",
    rhythm2026: "Find Your Rhythm Challenge 2026",
    rhythm2026s: "Find Your Rhythm Challenge 2026",
    november2026: "Milestones & Masterpieces 2026",
    november2026s: "Milestones & Masterpieces 2026",
  };

  const raceLabel =
    previewNames[raceName] ||
    raceName
      .replace(/[-_]/g, " ")
      .replace(/([a-z])([0-9])/gi, "$1 $2")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  const previewLegId = paramValue ? `price_${paramValue}` : "price_preview";

  return {
    selectedRace: {
      raceId: `preview_${raceName}`,
      legId: previewLegId,
      bibNumber: "PREVIEW",
      raceInfo: {
        name: raceLabel,
        legName: "Preview Distance",
        distance: "5",
      },
      giftCodes: null,
      boughtProducts: null,
    },
    raceData: {
      name: raceLabel,
      metadata: {
        bib_link: "#",
        fb_link: "#",
      },
    },
    legDetails: {},
  };
}

export function ConfirmationPage({ raceName }) {
  const { user, userData, userDetails, userRaces } = useUserContext();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const paramValue = queryParams.get("race");
  const previewRequested = queryParams.get("preview") === "1";
  const previewMode = previewRequested && isPreviewOrigin(window.location.origin);
  const [raceData, setRaceData] = useState(null);
  const [legDetails, setLegDetails] = useState(null);
  const [onboardingData, setOnboardingData] = useState(null);
  const [hasCompletedOnboardingBefore, setHasCompletedOnboardingBefore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasSentEvent, setHasSentEvent] = useState(false);
  const [previewSelectedRace, setPreviewSelectedRace] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [welcomeDone, setWelcomeDone] = useState(false);
  const welcomeDecision = useRef(null);

  const registeredRace = useMemo(() => {
    if (typeof userRaces !== typeof new Map()) return null
    return userRaces?.get(`price_${paramValue}`);
  }, [paramValue, userRaces])
  const selectedRace = registeredRace ?? previewSelectedRace;
  // console.log(userRaces, paramValue, selectedRace);
  const firstName = useMemo(() => {
    if (previewMode) return "Preview"
    if (!userDetails) return null
    if (!userDetails?.firstName || typeof userDetails.firstName !== typeof "") return null
    return userDetails?.firstName.slice(0, 1).toUpperCase() + userDetails?.firstName.slice(1, userDetails?.firstName.length);
  }, [previewMode, userDetails])

  // console.log(raceData);

  async function getCurrentRace(raceId) {
    const raceDoc = await getDoc(doc(db, "stripe_products", raceId));
    setRaceData(withCommunityHome(raceDoc.data()));
  }
  async function getLegDetails(raceId) {
    const raceDoc = await getDoc(doc(db, "races", raceId));
    setLegDetails(raceDoc.data());
  }
  async function getOnboarding(onboardingID) {
    const onboardingDoc = await getDoc(doc(db, "onboarding", onboardingID));
    if (onboardingDoc.exists()) {
      setOnboardingData(onboardingDoc.data());
    }
  }

  const loadPreviewRace = useCallback(async () => {
    setPreviewError("");
    try {
      const raceQuery = query(collection(db, "stripe_products"), where("stripe_metadata_url", "==", raceName));
      const raceSnapshot = await getDocs(raceQuery);
      const raceDoc = raceSnapshot.docs[0];

      if (!raceDoc) {
        const fallback = buildPreviewFallback(raceName, paramValue);
        setPreviewSelectedRace(fallback.selectedRace);
        setRaceData(withCommunityHome(fallback.raceData));
        setLegDetails(fallback.legDetails);
        return;
      }

      const pricesSnapshot = await getDocs(query(collection(db, "stripe_products", raceDoc.id, "prices"), orderBy("unit_amount", "asc")));
      const priceDocs = pricesSnapshot.docs.filter((priceDoc) => priceDoc.data()?.active);
      const requestedPriceId = paramValue ? `price_${paramValue}` : "";
      const matchedPriceDoc = requestedPriceId
        ? priceDocs.find((priceDoc) => {
          const data = priceDoc.data();
          return priceDoc.id === requestedPriceId || data?.metadata?.doc_id === requestedPriceId;
        })
        : null;
      const previewPriceDoc = matchedPriceDoc || priceDocs[0] || pricesSnapshot.docs[0];

      if (!previewPriceDoc) {
        const fallback = buildPreviewFallback(raceName, paramValue);
        setPreviewSelectedRace(fallback.selectedRace);
        setRaceData(withCommunityHome({
          ...fallback.raceData,
          name: raceDoc.data()?.name || fallback.raceData.name,
        }));
        setLegDetails(fallback.legDetails);
        return;
      }

      const priceData = previewPriceDoc.data();
      const previewLegId = priceData?.metadata?.doc_id || previewPriceDoc.id;
      const legDoc = await getDoc(doc(db, "races", previewLegId));

      setRaceData(withCommunityHome(raceDoc.data()));
      setLegDetails(legDoc.exists() ? legDoc.data() : {});
      setPreviewSelectedRace({
        raceId: raceDoc.id,
        legId: previewLegId,
        bibNumber: "PREVIEW",
        raceInfo: {
          name: raceDoc.data()?.name || raceName,
          legName: priceData?.description || priceData?.nickname || raceDoc.data()?.name || raceName,
          distance: priceData?.metadata?.distance || "",
        },
        giftCodes: null,
        boughtProducts: null,
      });
    } catch (error) {
      console.error(error);
      setPreviewSelectedRace(null);
      setPreviewError(`Preview could not load "${raceName}".`);
      setLoading(false);
    }
  }, [paramValue, raceName]);

  useEffect(() => {
    if (isPreviewOrigin(window.location.origin)) {
    } else {
      // eslint-disable-next-line no-undef
      fbq("track", "Purchase", { value: 71, currency: "CAD" });
    }
  }, []);

  useEffect(() => {
    if (registeredRace || !previewMode) return;
    setLoading(true);
    loadPreviewRace();
  }, [loadPreviewRace, previewMode, registeredRace]);

  useEffect(() => {
    if (previewMode) return;
    if (selectedRace) {
      getCurrentRace(selectedRace.raceId);
      getLegDetails(selectedRace.legId);
    }
  }, [previewMode, selectedRace]);

  useEffect(() => {
    async function Init() {
      if (previewMode) {
        setHasCompletedOnboardingBefore(true);
        setLoading(false);
        return;
      }
      if (legDetails?.onboarding) {
        let answersDoc = await getDoc(doc(db, "races", selectedRace.legId, "onboarding", user.uid));
        setHasCompletedOnboardingBefore(answersDoc.exists());
        if (!answersDoc.exists()) {
          await getOnboarding(legDetails.onboarding);
        }
      } else {
        setHasCompletedOnboardingBefore(true);
      }
      setLoading(false);
    }
    if (legDetails !== null) Init();
  }, [legDetails, previewMode, selectedRace, user]);

  if (previewError) {
    return (
      <div className="mt-5">
        <p>{previewError}</p>
        <p>
          Try opening <code>?preview=1</code> on a valid confirmation route, for example{" "}
          <code>{`/register/${raceName}/confirmation?preview=1`}</code>.
        </p>
      </div>
    );
  }

  // raceData stays null until its fetch finishes (a missing doc leaves it undefined).
  if (!selectedRace || loading || raceData === null || (onboardingData === null && !hasCompletedOnboardingBefore)) {
    return (
      <div className="mt-5">
        <Spinner />
      </div>
    );
  }
  if (!previewMode && !hasSentEvent && user?.uid) {
    SendRegistration(user.uid, selectedRace?.raceInfo?.name, selectedRace?.raceInfo?.legName);
    if (isInviteVisit(location.search)) {
      SendAnalyticsEvent("race_invite_referred_registration", { race: raceName, legName: selectedRace?.raceInfo?.legName, referral_link: queryParams.get("referral_link") || "" });
    }
    setHasSentEvent(true);
  }

  if (onboardingData !== null && typeof onboardingData === "object" && !hasCompletedOnboardingBefore) {
    // Set page title
    document.title = "Onboarding - " + raceData?.name;
    SendPageView("Onboarding - " + raceData?.name);
    return (
      <Onboarding
        onboardingID={legDetails.onboarding}
        pollData={onboardingData}
        selectedRace={selectedRace}
        firstName={firstName}
        setHasCompletedOnboardingBefore={setHasCompletedOnboardingBefore}
      ></Onboarding>
    );
  }

  const previewBanner = previewMode ? (
    <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      Preview mode is on. This confirmation page is using sample race data so you can inspect the UI without registering.
    </div>
  ) : null;

  // Races with a `referralCard` in their Stripe metadata get a welcome page before the confirmation page,
  // shown only once. The decision is made on first load so saving "seen" doesn't hide it mid-visit.
  const welcomeTheme = getWelcomeTheme(raceData);
  if (welcomeDecision.current === null) {
    welcomeDecision.current = Boolean(welcomeTheme && (previewMode || !selectedRace?.raceReady?.welcomeSeenAt));
  }
  if (welcomeDecision.current && !welcomeDone) {
    document.title = `Welcome - ${raceData?.name}`;
    SendPageView(`Welcome - ${raceData?.name}`);
    return (
      <>
        <WelcomeInvite
          theme={welcomeTheme}
          firstName={firstName}
          selectedRace={selectedRace}
          raceName={raceName}
          previewMode={previewMode}
          onContinue={() => {
            setWelcomeDone(true);
            window.scrollTo(0, 0);
          }}
        />
        {previewBanner}
      </>
    );
  }

  const raceReadyValue = {
    hero: {
      // Same logo as the welcome page when the race has a theme, otherwise the race's own logo.
      logoUrl: welcomeTheme ? `${process.env.PUBLIC_URL || ""}${welcomeTheme.logoUrl}` : selectedRace?.raceInfo?.imageUrl || raceData?.images?.[0],
      firstName,
      raceName: getDisplayRaceName(raceData, selectedRace),
    },
    checklist: (
      <RaceReadyChecklist
        selectedRace={selectedRace}
        raceData={raceData}
        raceName={raceName}
        imagePreset={getImagePreset(selectedRace, raceData, legDetails)}
        inviteTheme={welcomeTheme}
        previewMode={previewMode}
      />
    ),
  };

  // Set page title
  document.title = "Confirmation - " + raceData?.name;
  SendPageView("Confirmation - " + raceData?.name);

  const racePage = (() => {
  switch (raceName) {
    case "BigCanadaRunStaging":
    case "BigCanadaRun":
    case "thebigcanadarun2023":
    case "thebigcanadarun2026s":
    case "thebigcanadarun2026":
      return (
        <>
          <BCRConfirmation firstName={firstName} selectedRace={selectedRace} raceData={raceData} />
          {previewBanner}
        </>
      );

    case "thebigcanadarun2024":
      //case "tbcr24s":
      return (
        <>
          <BCR2024 firstName={firstName} selectedRace={selectedRace} raceData={raceData} />
          {previewBanner}
        </>
      );

    case "thebigcanadarun2025":
      // case "tbcr24s":
      return (
        <>
          <BCR2025 firstName={firstName} selectedRace={selectedRace} raceData={raceData} />
          {previewBanner}
        </>
      );

    case "ride2025":
      // case "tbcr24s":
      return (
        <>
          <RIDE2025 firstName={firstName} selectedRace={selectedRace} raceData={raceData} />
          {previewBanner}
        </>
      );

    case "fall2026":
    case "fall2026s":
      return (
        <>
          <FALL2026 firstName={firstName} selectedRace={selectedRace} raceData={raceData} />
          {previewBanner}
        </>
      );

    case "fall2025":
    case "tbcr24s":
      return (
        <>
          <FALL2025 firstName={firstName} selectedRace={selectedRace} raceData={raceData} />
          {previewBanner}
        </>
      );

    case "60northrun2024":
    case "60northrun":
      return (
        <>
          <NorthRunConfirmation firstName={firstName} selectedRace={selectedRace} raceData={raceData} />
          {previewBanner}
        </>
      );

    case "60northrun25":
      return (
        <>
          <NorthRun2025 firstName={firstName} selectedRace={selectedRace} raceData={raceData} />
          {previewBanner}
        </>
      );

    case "60northrun26":
      return (
        <>
          <NorthRun26 firstName={firstName} selectedRace={selectedRace} raceData={raceData} />
          {previewBanner}
        </>
      );

    case "february24":
    case "february26":
    case "february26s":
      return (
        <>
          <ISWFC24 firstName={firstName} selectedRace={selectedRace} raceData={raceData} raceName={raceName} />
          {previewBanner}
        </>
      );

    case "march2026":
    case "march2026s":
      return (
        <>
          <MARCH firstName={firstName} selectedRace={selectedRace} raceData={raceData} raceName={raceName} />
          {previewBanner}
        </>
      );

    case "bloom2026":
    case "bloom2026s":
      return (
        <>
          <BLOOM firstName={firstName} selectedRace={selectedRace} raceData={raceData} raceName={raceName} />
          {previewBanner}
        </>
      );

    case "lakes2026":
    case "lakes2026s":
      return (
        <>
          <LAKES firstName={firstName} selectedRace={selectedRace} raceData={raceData} />
          {previewBanner}
        </>
      );

    case "nature2026":
    case "nature2026s":
      return (
        <>
          <NATURE firstName={firstName} selectedRace={selectedRace} raceData={raceData} />
          {previewBanner}
        </>
      );

    case "spark2026":
    case "spark2026s":
      return (
        <>
          <SPARK firstName={firstName} selectedRace={selectedRace} raceData={raceData} />
          {previewBanner}
        </>
      );

    case "rhythm2026":
    case "rhythm2026s":
      return (
        <>
          <RHYTHM firstName={firstName} selectedRace={selectedRace} raceData={raceData} />
          {previewBanner}
        </>
      );

    case "november2026":
    case "november2026s":
      return (
        <>
          <MILESTONES firstName={firstName} selectedRace={selectedRace} raceData={raceData} />
          {previewBanner}
        </>
      );

    case "chasing2026":
    case "chasing2026s":
      return (
        <>
          <CHASING firstName={firstName} selectedRace={selectedRace} raceData={raceData} />
          {previewBanner}
        </>
      );

    case "celebratecanada-c":
    case "celebratecanada-a1":
    case "celebratecanada-n":
    case "celebratecanada-a2":
      return (
        <>
          <CelebrateCanadaConfirmation firstName={firstName} selectedRace={selectedRace} raceData={raceData} userData={userData} />
          {previewBanner}
        </>
      );

    default:
      return (
        <>
          <CCConfirmation firstName={firstName} selectedRace={selectedRace} raceData={raceData} />
          {previewBanner}
        </>
      );
  }
  })();

  return <RaceReadyContext.Provider value={raceReadyValue}>{racePage}</RaceReadyContext.Provider>;
}

function BCR2024({ firstName, selectedRace, raceData, userData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);

  return (
    <ConfirmationLayout
      bannerSrc={raceData?.metadata?.checkoutBanner}
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        `Thrilled to have you officially registered for ${raceName}! ${selectedRace?.raceInfo?.legName || ""}`.trim(),
        "Get ready to embark on an unforgettable journey across Canada with your team. An exhilarating adventure filled with excitement and camaraderie awaits.",
        `The challenge officially starts on Canada Day. You have from July 1st, 2024 to June 30th, 2025 to complete ${Number(selectedRace?.raceInfo?.distance).toLocaleString()} kilometres with your team. Good luck!`,
      ]}
      helpEmail="race@thebigcanadarun.ca"
      closingText="Good luck with your challenge, and let's get ready to start the coveted adventure across Canada. This will be epic!"
      signature="The Big Canada Run Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description="Get your Big Canada Run dashboard ready before Canada Day so your progress tracking is set up for kickoff."
        note={
          <>
            On July 1st, you can start posting your kilometres manually or connect your tracking on the{" "}
            <Link to={`/dashboard/${dashboardId}`} className="font-medium text-[#c56d33] hover:underline">
              Big Canada Run Dashboard
            </Link>
            .
          </>
        }
      />

      <BibCommunityCard
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. Download your printable BIB{" "}
            <a href={raceData?.metadata?.bib_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>{" "}
            and share it on your socials.
          </>,
          <>
            <a href={raceData?.metadata?.fb_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              Visit the community home
            </a>{" "}
            for motivation, support, and lots of fun.
          </>,
          "Your medal and SWAG kit will ship mid-Fall.",
        ]}
        actions={[
          { href: raceData?.metadata?.bib_link, label: "Download BIB" },
          { href: raceData?.metadata?.fb_link, label: "Visit Community" },
        ].filter((action) => action.href)}
      />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription="If you purchased gift registrations, you can find them below and on your account settings page. Please pass the gift code to the intended recipient so they can use it to register for the challenge."
        giftGuideHref="https://scribehow.com/shared/How_to_Use_a_Gift_Code__The_Big_Canada_Run___ODwf_4oQK-b8yNyasCz3g"
        merchandiseText="If you purchased special edition socks, you will receive them with your medal."
      >
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && (
            <StrengthProgramBlock title="4-Week Strength Training Program" />
          )}
      </GiftProductsCard>

      <DashboardCtaCard
        description="Check your Big Canada Run dashboard to review your challenge details and get ready for the start line."
        to={`/dashboard/${dashboardId}`}
      />

      <ReferralCard
        description="Invite friends and family to join the challenge with your personal referral link."
        details="When people register using your link, they'll get $5 off and you'll unlock referral rewards."
      />
    </ConfirmationLayout>
  );
}

function BCR2025({ firstName, selectedRace, raceData, userData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);

  return (
    <ConfirmationLayout
      bannerSrc={raceData?.metadata?.checkoutBanner}
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        `Thrilled to have you officially registered for ${raceName}! ${selectedRace?.raceInfo?.legName || ""}`.trim(),
        "Get ready to embark on an unforgettable journey across Canada with your team. A uniquely Canadian adventure filled with motivation and support is waiting for you.",
        `The challenge officially starts on Canada Day. You have from July 1st, 2025 to June 30th, 2026 to complete ${Number(selectedRace?.raceInfo?.distance).toLocaleString()} kilometres with your team. Good luck!`,
      ]}
      helpEmail="race@thebigcanadarun.ca"
      closingText="Good luck with your challenge, and let's get ready to start the coveted adventure across Canada. This will be epic!"
      signature="The Big Canada Run Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description="Connect your sports watch or tracking app today so your kilometres start posting automatically on July 1st."
        note={
          <>
            You can connect it from your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              Account Settings page
            </Link>
            , or begin posting manually once the challenge starts.
          </>
        }
      />

      <BibCommunityCard
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. Download your printable BIB{" "}
            <a href={raceData?.metadata?.bib_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>{" "}
            and share it on your socials.
          </>,
          <>
            <a href={raceData?.metadata?.fb_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              Visit the community home
            </a>{" "}
            for motivation, support, and lots of fun.
          </>,
          "Your medal and SWAG kit will ship mid-Fall.",
        ]}
        actions={[
          { href: raceData?.metadata?.bib_link, label: "Download BIB" },
          { href: raceData?.metadata?.fb_link, label: "Visit Community" },
        ].filter((action) => action.href)}
      />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription="If you purchased gift registrations, you can find them below and on your account settings page. Please pass the gift code to the intended recipient so they can use it to register for the challenge."
        giftGuideHref="https://scribehow.com/shared/How_to_Use_a_Gift_Code__The_Big_Canada_Run___ODwf_4oQK-b8yNyasCz3g"
        merchandiseText="If you purchased special edition merchandise, you will receive it with your medal."
      >
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && (
            <StrengthProgramBlock title="4-Week Strength Training Program" />
          )}
      </GiftProductsCard>

      <DashboardCtaCard
        description="Open your dashboard to review your challenge details and be ready for the Canada Day kickoff."
        to={`/dashboard/${dashboardId}`}
      />

      <ReferralCard
        description="Invite friends and family to join the challenge with your personal referral link."
        details="When people register using your link, they'll get $5 off and you'll unlock referral rewards."
      />
    </ConfirmationLayout>
  );
}

function RIDE2025({ firstName, selectedRace, raceData, userData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);

  return (
    <ConfirmationLayout
      bannerSrc={raceData?.metadata?.checkoutBanner}
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        `Thrilled to have you officially registered for ${raceName}! ${selectedRace?.raceInfo?.legName || ""}`.trim(),
        "Get ready to embark on an unforgettable journey across Canada with your team. A uniquely Canadian adventure filled with motivation and support is waiting for you.",
        `The challenge officially starts on Canada Day. You have from July 1st, 2025 to October 31st, 2025 to complete ${Number(selectedRace?.raceInfo?.distance).toLocaleString()} kilometres with your team. Good luck!`,
      ]}
      helpEmail="race@thebigcanadarun.ca"
      closingText="Good luck with your challenge, and let's get ready to start the coveted adventure across Canada. This will be epic!"
      signature="The Big Canada Ride Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description="Connect your sports watch or tracking app today so your kilometres start posting automatically on July 1st."
        note={
          <>
            You can connect it from your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              Account Settings page
            </Link>
            , or begin posting manually once the challenge starts.
          </>
        }
      />

      <BibCommunityCard
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. Download your printable BIB{" "}
            <a href={raceData?.metadata?.bib_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>{" "}
            and share it on your socials.
          </>,
          <>
            <a href={raceData?.metadata?.fb_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              Visit the community home
            </a>{" "}
            for motivation, support, and lots of fun.
          </>,
          "Your medal and SWAG kit will ship mid-Fall.",
        ]}
        actions={[
          { href: raceData?.metadata?.bib_link, label: "Download BIB" },
          { href: raceData?.metadata?.fb_link, label: "Visit Community" },
        ].filter((action) => action.href)}
      />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription="If you purchased gift registrations, you can find them below and on your account settings page. Please pass the gift code to the intended recipient so they can use it to register for the challenge."
        giftGuideHref="https://scribehow.com/shared/How_to_Use_a_Gift_Code__The_Big_Canada_Run___ODwf_4oQK-b8yNyasCz3g"
        merchandiseText="If you purchased special edition merchandise, you will receive it with your medal."
      >
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && (
            <StrengthProgramBlock title="4-Week Strength Training Program" />
          )}
      </GiftProductsCard>

      <DashboardCtaCard
        description="Open your dashboard to review your challenge details and set up your progress tracking."
        to={`/dashboard/${dashboardId}`}
      />

      <ReferralCard
        description="Invite friends and family to join the ride with your personal referral link."
        details="When people register using your link, they'll get $5 off and you'll unlock referral rewards."
      />
    </ConfirmationLayout>
  );
}

function FALL2025({ firstName, selectedRace, raceData, userData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);

  return (
    <ConfirmationLayout
      bannerSrc={raceData?.metadata?.checkoutBanner}
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        "It is great to have you on board. We are looking forward to seeing you conquer Fall, get ready for winter, and create unforgettable memories along the way.",
        "The challenge officially starts on Wednesday, October 1st, 2025 and ends on Sunday, November 30th, 2025. Good luck!",
      ]}
      helpEmail="race@thebigcanadarun.ca"
      closingText="Good luck with your challenge."
      signOff=""
      signature="The Joy of Fall Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description="Set up your progress tracking before October 1st so your activities can start posting automatically on day one."
        note={
          <>
            You can do that on your{" "}
            <Link to={`/dashboard/${dashboardId}`} className="font-medium text-[#c56d33] hover:underline">
              dashboard
            </Link>
            . If you have any questions about tracking, there is also a{" "}
            <a href="https://mychallengecanada.ca/fall-faqs/#tracking" target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              comprehensive resource here
            </a>
            .
          </>
        }
      />

      <BibCommunityCard
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. Download your printable BIB{" "}
            <a href={raceData?.metadata?.bib_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>{" "}
            and share it on your socials.
          </>,
          "Your medal and SWAG kit will ship mid-November.",
        ]}
        actions={[{ href: raceData?.metadata?.bib_link, label: "Download BIB" }].filter((action) => action.href)}
      />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription="If you purchased gift registrations, you can find them below and on your account settings page. Please pass the gift code to the intended recipient so they can use it to register for the challenge."
        giftGuideHref="https://scribehow.com/shared/How_to_Use_a_Gift_Code__The_Big_Canada_Run___ODwf_4oQK-b8yNyasCz3g"
        merchandiseText="If you purchased special edition socks, you will receive them with your medal."
      >
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && (
            <StrengthProgramBlock title="4-Week Strength Training Program" />
          )}
      </GiftProductsCard>

      <DashboardCtaCard
        description="Check your challenge dashboard to activate tracking and keep all your Fall challenge details in one place."
        to={`/dashboard/${dashboardId}`}
      />

      <ReferralCard
        description="Invite friends and family to join the challenge with your personal referral link."
        details="When people register using your link, they'll get $5 off and you'll unlock referral rewards."
      />
    </ConfirmationLayout>
  );
}

function BCRConfirmation({ firstName, selectedRace, raceData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);

  return (
    <ConfirmationLayout
      bannerSrc={raceData?.metadata?.checkoutBanner}
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        "Get ready to embark on an unforgettable journey across Canada with your team. A uniquely Canadian adventure filled with motivation and support is waiting for you.",
        <strong> The challenge officially starts on Canada Day.</strong>,
        `You have from July 1st, 2026 to June 30th, 2027 to complete ${selectedRace?.raceInfo?.distance} km. Good luck!`,
      ]}
      helpEmail="hello@mychallengecanada.ca"
      closingText="Good luck with your challenge, and let's get ready to start the coveted adventure across Canada. This will be epic!"
      signature="The Big Canada Run Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description="You’ll be able to start logging your kilometres on July 1st, either manually or by syncing your sports watch or tracking app.
"
        note={
          <>
            Visit your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              Account Settings
            </Link>
            {" "}page today to connect your device ahead of the challenge, upload your profile picture, and confirm that your shipping address is complete and up to date, as this is the address we'll use to mail your medal.
          </>
        }
      />

      <BibCommunityCard
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. To download the printable BIB,{" "}
            <a href={raceData?.metadata?.bib_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>
            . Print it, personalize it, and share it in the Social Feed!
          </>,
          <>
            Check out the Social Feed {" "}
            <a href="/" target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>
            {" "} and introduce yourself.

          </>,
          "Your medal and SWAG kit will ship mid-Fall.",
        ]}
        actions={[
          { href: raceData?.metadata?.bib_link, label: "Download BIB" },
          { href: raceData?.metadata?.fb_link, label: "Visit Community" },
        ].filter((action) => action.href)}
      />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription={
          <>
            If you purchased gift registrations, you can find them below and on your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              Account Settings
            </Link>
            {" "}page. There, you will see which codes have been redeemed and which ones are still available.
          </>
        }
        merchandiseText="If you purchased special edition merchandise (socks, bracelets), you will receive them with your medal in the Fall.
"
      >
        {selectedRace?.boughtProducts && Object.values(selectedRace.boughtProducts).includes("101 Body-Weight Exercises") && (
          <BodyWeightProgramBlock />
        )}
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && <StrengthProgramBlock />}
      </GiftProductsCard>

      <DashboardCtaCard
        description="Open your dashboard to review your challenge details and get ready to cross Canada with your team."
        to={`/dashboard/${dashboardId}`}
      />
    </ConfirmationLayout>
  );
}

function CelebrateCanadaConfirmation({ firstName, selectedRace, raceData, userData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);
  const challengeStartDate = raceData?.metadata?.userStartDate || selectedRace?.raceInfo?.userStartDate || "August 15th, 2026";
  const challengeEndDate = raceData?.metadata?.userEndDate || selectedRace?.raceInfo?.userEndDate || "December 31st, 2026";
  const challengeWindow = `You have from ${challengeStartDate} to ${challengeEndDate} to complete the ${selectedRace?.raceInfo?.distance} km challenge and post your kilometres on the platform. Good luck!`;
  const trackingDescription = `Connect your sports watch or tracking app today so your kilometres start posting automatically on ${challengeStartDate}.`;
  const communityAction = raceData?.metadata?.fb_link
    ? { href: raceData.metadata.fb_link, label: "Visit Community" }
    : { to: "/", label: "Visit Community" };

  return (
    <ConfirmationLayout
      bannerSrc={raceData?.metadata?.checkoutBanner}
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        `We are looking forward to seeing you crush this challenge and celebrate Canada's birthday in an epic way. You are all set to participate in the Celebrate Canada ${selectedRace?.raceInfo?.distance} km Challenge.`,
        challengeWindow,
      ]}
      helpEmail="hello@mychallengecanada.ca"
      closingText="Good luck with your challenge, and let's Celebrate Canada together!"
      signature="The My Challenge Canada Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description={trackingDescription}
        note={
          <>
            You can connect it from your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              Account Settings page
            </Link>{" "}
            , or begin posting manually once the challenge starts.
          </>
        }
      />

      <BibCommunityCard
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. Download your printable BIB{" "}
            <a href={raceData?.metadata?.bib_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>{" "}
            and share it on your socials.
          </>,
          raceData?.metadata?.fb_link ? (
            <>
              Join the{" "}
              <a href={raceData.metadata.fb_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
                community
              </a>{" "}
              for motivation, support, and lots of fun.
            </>
          ) : (
            <>
              Don't forget to introduce yourself in the{" "}
              <Link to="/" className="font-medium text-[#c56d33] hover:underline">
                Community
              </Link>
              . Your fellow participants are waiting with motivation, support, and lots of fun.
            </>
          ),
          "We will begin shipping medals to finishers in late November.",
        ]}
        actions={[{ href: raceData?.metadata?.bib_link, label: "Download BIB" }, communityAction].filter(
          (action) => action.href || action.to
        )}
      />

      <ResourceLinksCard title="Health and Fitness Bundle" intro="To access your Health and Fitness Bundle, click on the links below:" links={HEALTH_AND_FITNESS_BUNDLE_LINKS} />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription="If you purchased gift registrations, you can find them below and on your account settings page."
        merchandiseText="If you purchased special edition socks, you will receive them with your medal."
      >
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && <StrengthProgramBlock />}
      </GiftProductsCard>

      <DashboardCtaCard
        description="Open your Celebrate Canada dashboard to track progress and keep your challenge details close at hand."
        to={`/dashboard/${dashboardId}`}
      />
    </ConfirmationLayout>
  );
}

function CCConfirmation({ firstName, selectedRace, raceData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);

  return (
    <ConfirmationLayout
      bannerSrc={raceData?.metadata?.checkoutBanner}
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        "We are looking forward to seeing you crush this challenge and celebrate Canada's birthday in an epic way!",
        "You have from April 15th, 2023 to July 1st, 2023 to complete the 156 km and finish the challenge. Good luck!",
      ]}
      helpEmail="hello@mychallengecanada.ca"
      closingText="Good luck with your challenge, and let's Celebrate Canada together!"
      signature="The My Challenge Canada Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description="You can start posting your kilometres manually or sync your sports watch or tracking app today."
        note={
          <>
            Visit your{" "}
            <Link to={`/dashboard/${dashboardId}`} className="font-medium text-[#c56d33] hover:underline">
              Celebrate Canada dashboard
            </Link>{" "}
            to get started.
          </>
        }
      />

      <BibCommunityCard
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. Download your printable BIB{" "}
            <a href={raceData?.metadata?.bib_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>
            .
          </>,
          <>
            Visit the{" "}
            <a href={raceData?.metadata?.fb_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              community home
            </a>{" "}
            for motivation, support, and lots of fun.
          </>,
          "We will start shipping the first batch of medals early-June.",
        ]}
        actions={[
          { href: raceData?.metadata?.bib_link, label: "Download BIB" },
          { href: raceData?.metadata?.fb_link, label: "Visit Community" },
        ].filter((action) => action.href)}
      />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription="If you purchased gift registrations, you can find them below and on your account settings page."
        merchandiseText="If you purchased special edition socks, you will receive them with your medal."
      >
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && <StrengthProgramBlock />}
      </GiftProductsCard>

      <DashboardCtaCard
        description="Open your Celebrate Canada dashboard to keep your challenge details and tracking in one place."
        to={`/dashboard/${dashboardId}`}
      />
    </ConfirmationLayout>
  );
}

function NorthRunConfirmation({ firstName, selectedRace, raceData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);

  return (
    <ConfirmationLayout
      bannerSrc={raceData?.metadata?.checkoutBanner}
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        "We are looking forward to seeing you beat winter and crush this challenge.",
        "The challenge officially starts on Monday, January 1st, 2024 and ends on Wednesday, January 31st, 2024.",
      ]}
      helpEmail="race@60northrun.ca"
      closingText="Good luck with your challenge, and let's beat winter together as true Winter Warriors!"
      signature="60 North Run Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description="Get your dashboard ready before January 1st so your progress tracking is set up for kickoff."
        note={
          <>
            On January 1st, you can start posting manually or sync your sports watch or tracking app on your{" "}
            <Link to={`/dashboard/${dashboardId}`} className="font-medium text-[#c56d33] hover:underline">
              60 North Run dashboard
            </Link>
            .
          </>
        }
      />

      <BibCommunityCard
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. Download your printable BIB{" "}
            <a href={raceData?.metadata?.bib_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>
            .
          </>,
          <>
            Visit the{" "}
            <a href={raceData?.metadata?.fb_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              community home
            </a>{" "}
            for motivation, support, and lots of fun.
          </>,
          "Your medal and SWAG kit will ship mid-February.",
        ]}
        actions={[
          { href: raceData?.metadata?.bib_link, label: "Download BIB" },
          { href: raceData?.metadata?.fb_link, label: "Visit Community" },
        ].filter((action) => action.href)}
      />

      <ResourceLinksCard title="Health and Fitness Bundle" intro="To access your Health and Fitness Bundle, click on the links below:" links={HEALTH_AND_FITNESS_BUNDLE_LINKS} />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription="If you purchased gift registrations, you can find them below and on your account settings page."
        merchandiseText="If you purchased special edition socks, you will receive them with your medal."
      >
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && <StrengthProgramBlock />}
      </GiftProductsCard>

      <DashboardCtaCard
        description="Open your 60 North Run dashboard to review your challenge details and be ready for your winter kickoff."
        to={`/dashboard/${dashboardId}`}
      />

      <ReferralCard
        description="Invite friends and family to join the challenge with your personal referral link."
        details="When people register using your link, they'll get $5 off and you'll unlock referral rewards."
        to={`${process.env.PUBLIC_URL}/referralhub`}
      />
    </ConfirmationLayout>
  );
}

function ISWFC24({ firstName, selectedRace, raceData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);

  return (
    <ConfirmationLayout
      bannerSrc={raceData?.metadata?.checkoutBanner}
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        "We look forward to seeing you tackle this challenge and keep conquering winter in an epic way.",
        <>
          This challenge consists of 11 trails all over Canada, adding up to 126 km. You will earn badges when you complete each trail. The
          first trail is the Auriol Trail in Kluane National Park, Yukon, and you can learn more about it{" "}
          <a
            href="https://60northrun.ca/auriol-trail-kluane-national-park-yukon/"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-[#c56d33] hover:underline"
          >
            here
          </a>
          .
        </>,
        "You have from February 1st, 2026 to February 28th, 2026 to complete the challenge. Good luck!",
      ]}
      helpEmail="race@60northrun.ca"
      closingText="Good luck with your challenge, and let's keep the Winter Warrior motivation alive!"
      signature="60 North Run Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description="Once the challenge starts on February 1st, you can post your kilometres manually or sync your sports watch or Strava account."
        note={
          <>
            Use your{" "}
            <Link to={`/dashboard/${dashboardId}`} className="font-medium text-[#c56d33] hover:underline">
              challenge dashboard
            </Link>{" "}
            to keep your trail progress moving.
          </>
        }
      />

      <BibCommunityCard
        title="BIB and Challenge Info"
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. Download the printable BIB{" "}
            <a
              href="https://drive.google.com/file/d/1BqI8VEI4ycvOWqvBVhF3jqO3_TWJMsFr/view?usp=sharing"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-[#c56d33] hover:underline"
            >
              here
            </a>{" "}
            and share it on your socials.
          </>,
          "Each completed trail unlocks the next stop on your cross-Canada winter adventure.",
        ]}
        actions={[{ href: "https://drive.google.com/file/d/1BqI8VEI4ycvOWqvBVhF3jqO3_TWJMsFr/view?usp=sharing", label: "Download BIB" }]}
      />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription="If you purchased gift registrations, you can find them below and on your account settings page."
        merchandiseText="If you purchased special edition socks, you will receive them with your medal."
      >
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && <StrengthProgramBlock />}
      </GiftProductsCard>

      <DashboardCtaCard
        description="Open your challenge dashboard to follow your trail progression and keep all your February challenge details in one place."
        to={`/dashboard/${dashboardId}`}
      />
    </ConfirmationLayout>
  );
}

function NorthRun2025({ firstName, selectedRace, raceData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);

  return (
    <ConfirmationLayout
      bannerSrc={raceData?.metadata?.checkoutBanner}
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        "We are looking forward to seeing you beat winter and crush this challenge.",
        "The challenge officially starts on Wednesday, January 1st, 2025 and ends on Friday, January 31st, 2025.",
      ]}
      helpEmail="race@60northrun.ca"
      closingText="Good luck with your challenge, and let's beat winter together as true Winter Warriors!"
      signature="60 North Run Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description="Connect your sports watch or tracking app today so your kilometres start posting automatically on January 1st."
        note={
          <>
            You can connect it from your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              60° North Run Account Settings page
            </Link>
            , or begin posting manually once the challenge starts.
          </>
        }
      />

      <BibCommunityCard
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. Download your printable BIB{" "}
            <a href={raceData?.metadata?.bib_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>{" "}
            and share it on your socials.
          </>,
          <>
            Visit the{" "}
            <a href={raceData?.metadata?.fb_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              community home
            </a>{" "}
            for motivation, support, and lots of fun.
          </>,
          "Your medal and SWAG kit will ship mid-February.",
        ]}
        actions={[
          { href: raceData?.metadata?.bib_link, label: "Download BIB" },
          { href: raceData?.metadata?.fb_link, label: "Visit Community" },
        ].filter((action) => action.href)}
      />

      <ResourceLinksCard
        title="Health and Fitness Bundle"
        intro="To access your Health and Fitness Bundle, click on the links below:"
        links={HEALTH_AND_FITNESS_BUNDLE_LINKS}
      />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription="If you purchased gift registrations, you can find them below and on your account settings page."
        merchandiseText="If you purchased special edition socks, you will receive them with your medal."
      >
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && <StrengthProgramBlock />}
      </GiftProductsCard>

      <DashboardCtaCard
        description="Open your 60° North Run dashboard to activate tracking and keep your winter challenge details in one place."
        to={`/dashboard/${dashboardId}`}
      />

      <ReferralCard
        description="Invite friends and family to join the challenge with your personal referral link."
        details="When people register using your link, they'll get $5 off and you'll unlock referral rewards."
      />
    </ConfirmationLayout>
  );
}

function NorthRun26({ firstName, selectedRace, raceData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);

  return (
    <ConfirmationLayout
      bannerSrc={raceData?.metadata?.checkoutBanner}
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        "We are looking forward to seeing you beat winter and crush this challenge.",
        "The challenge officially starts on Thursday, January 1st, 2026 and ends on Saturday, January 31st, 2026.",
      ]}
      helpEmail="race@60northrun.ca"
      closingText="Good luck with your challenge, and let's beat winter together as true Winter Warriors!"
      signature="60 North Run Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description="Connect your sports watch or tracking app today so your kilometres start posting automatically on January 1st."
        note={
          <>
            You can connect it from your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              60° North Run Account Settings page
            </Link>
            , or begin posting manually once the challenge starts.
          </>
        }
      />

      <BibCommunityCard
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. Download your printable BIB{" "}
            <a href={raceData?.metadata?.bib_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>{" "}
            and share it on your socials.
          </>,
          <>
            Don't forget to introduce yourself in the{" "}
            <Link to="/" className="font-medium text-[#c56d33] hover:underline">
              Community
            </Link>
            . Your fellow participants are waiting for you with motivation, support, and lots of fun.
          </>,
          "Your medal and SWAG kit will ship mid-February.",
        ]}
        actions={[
          { href: raceData?.metadata?.bib_link, label: "Download BIB" },
          { to: "/", label: "Visit Community" },
        ]}
      />

      <ResourceLinksCard
        title="Health and Fitness Bundle"
        intro="To access your Health and Fitness Bundle, click on the links below:"
        links={HEALTH_AND_FITNESS_BUNDLE_LINKS}
      />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription="If you purchased gift registrations, you can find them below and on your account settings page."
        merchandiseText="If you purchased special edition socks, you will receive them with your medal."
      >
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && <StrengthProgramBlock />}
      </GiftProductsCard>

      <DashboardCtaCard
        description="Open your 60° North Run dashboard to activate tracking and keep your winter challenge details in one place."
        to={`/dashboard/${dashboardId}`}
      />

      <ReferralCard
        description="Invite friends and family to join the challenge with your personal referral link."
        details="When people register using your link, they'll get $5 off and you'll unlock referral rewards."
      />
    </ConfirmationLayout>
  );
}

function MARCH({ firstName, selectedRace, raceData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);
  const communityAction = raceData?.metadata?.fb_link
    ? { href: raceData.metadata.fb_link, label: "Visit Community" }
    : { to: "/", label: "Visit Community" };

  return (
    <ConfirmationLayout
      bannerSrc={
        raceData?.metadata?.checkoutBanner ||
        "https://firebasestorage.googleapis.com/v0/b/bigtimerun-4c245.appspot.com/o/banners%2Fcheckout%2FMarch-2026.png?alt=media&token=de85f786-343e-42bb-83b4-c807a84fecb4"
      }
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        "We are looking forward to seeing you have fun in this transition month.",
        "The challenge officially starts on Sunday, March 1st, 2026 and ends on Tuesday, March 31st, 2026.",
      ]}
      helpEmail="race@60northrun.ca"
      closingText="Good luck with your challenge, and let's stay healthy and fit!"
      signature="The Momentum Membership Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description="Connect your sports watch or tracking app today so your kilometres start posting automatically on March 1st."
        note={
          <>
            You can connect it from your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              Account Settings page
            </Link>
            , or begin posting manually once the challenge starts.
          </>
        }
      />

      <BibCommunityCard
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. Download your printable BIB{" "}
            <a href={raceData?.metadata?.bib_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>{" "}
            and share it on your socials.
          </>,
          raceData?.metadata?.fb_link ? (
            <>
              Join the{" "}
              <a href={raceData.metadata.fb_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
                community
              </a>{" "}
              for motivation, support, and lots of fun.
            </>
          ) : (
            <>
              Don't forget to introduce yourself in the{" "}
              <Link to="/" className="font-medium text-[#c56d33] hover:underline">
                Community
              </Link>
              . Your fellow participants are waiting for you with motivation, support, and lots of fun.
            </>
          ),
        ]}
        actions={[{ href: raceData?.metadata?.bib_link, label: "Download BIB" }, communityAction].filter(
          (action) => action.href || action.to
        )}
      />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription="If you purchased gift registrations, you can find them below and on your account settings page."
      >
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && <StrengthProgramBlock />}
      </GiftProductsCard>

      <DashboardCtaCard
        description="Check your challenge dashboard to activate automatic distances and create your Slush & Sweat participant image."
        to={`/dashboard/${dashboardId}`}
      />
    </ConfirmationLayout>
  );
}

function BLOOM({ firstName, selectedRace, raceData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);
  const communityAction = raceData?.metadata?.fb_link
    ? { href: raceData.metadata.fb_link, label: "Visit Community" }
    : { to: "/", label: "Visit Community" };

  return (
    <ConfirmationLayout
      bannerSrc={raceData?.metadata?.checkoutBanner}
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        `We are looking forward to seeing you build momentum this spring and crush ${raceName}.`,
        "You have from April 1st, 2026 to April 30th, 2026 to complete the challenge and post your kilometres on the platform. Good luck!",
      ]}
      helpEmail="race@60northrun.ca"
      closingText="Good luck with your challenge, and let's stay healthy and consistent!"
      signature="The Momentum Membership Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description="Connect your sports watch or tracking app today so your kilometres start posting automatically on April 1st."
        note={
          <>
            You can connect it from your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              Account Settings page
            </Link>
            , or begin posting manually once the challenge starts.
          </>
        }
      />

      <BibCommunityCard
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. Download your printable BIB{" "}
            <a href={raceData?.metadata?.bib_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>{" "}
            and share it on your socials.
          </>,
          raceData?.metadata?.fb_link ? (
            <>
              Join the{" "}
              <a href={raceData.metadata.fb_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
                community
              </a>{" "}
              for motivation, support, and lots of fun.
            </>
          ) : (
            <>
              Don't forget to introduce yourself in the{" "}
              <Link to="/" className="font-medium text-[#c56d33] hover:underline">
                Community
              </Link>
              . Your fellow participants are waiting for you with motivation, support, and lots of fun.
            </>
          ),
        ]}
        actions={[{ href: raceData?.metadata?.bib_link, label: "Download BIB" }, communityAction].filter(
          (action) => action.href || action.to
        )}
      />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription={
          <>
            If you purchased gift registrations, you can find the codes below and on your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              account settings page
            </Link>
            .
          </>
        }
      >
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && <StrengthProgramBlock />}
      </GiftProductsCard>

      <DashboardCtaCard
        description={`Check your ${raceName} dashboard to review your challenge details and get your tracking ready.`}
        to={`/dashboard/${dashboardId}`}
      />
    </ConfirmationLayout>
  );
}

function LAKES({ firstName, selectedRace, raceData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);
  const challengeStartDate = selectedRace?.raceInfo?.userStartDate || raceData?.metadata?.userStartDate || "";
  const challengeEndDate = selectedRace?.raceInfo?.userEndDate || raceData?.metadata?.userEndDate || "";
  const challengeWindow =
    challengeStartDate && challengeEndDate
      ? `You have from ${challengeStartDate} to ${challengeEndDate} to complete the challenge and post your kilometres on the platform. Good luck!`
      : "Complete the challenge by posting your kilometres on the platform. Good luck!";
  const trackingDescription = challengeStartDate
    ? `Connect your sports watch or tracking app today so your kilometres start posting automatically on ${challengeStartDate}.`
    : "Connect your sports watch or tracking app today so your kilometres are ready to post when the challenge starts.";
  const communityAction = raceData?.metadata?.fb_link
    ? { href: raceData.metadata.fb_link, label: "Visit Community" }
    : { to: "/", label: "Visit Community" };

  return (
    <ConfirmationLayout
      bannerSrc={raceData?.metadata?.checkoutBanner}
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        `We are looking forward to seeing you build summer momentum and make waves with ${raceName}.`,
        challengeWindow,
      ]}
      helpEmail="race@mychallengecanada.ca"
      closingText="Good luck with your challenge, and let's keep making waves all summer long!"
      signature="The My Challenge Canada Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description={trackingDescription}
        note={
          <>
            You can connect it from your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              Account Settings page
            </Link>
            , or begin posting manually once the challenge starts.
          </>
        }
      />

      <BibCommunityCard
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. Download your printable BIB{" "}
            <a href={raceData?.metadata?.bib_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>{" "}
            and share your lake-run summer vibe on your socials.
          </>,
          raceData?.metadata?.fb_link ? (
            <>
              Join the{" "}
              <a href={raceData.metadata.fb_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
                community
              </a>{" "}
              for motivation, support, and lots of fun.
            </>
          ) : (
            <>
              Don't forget to introduce yourself in the{" "}
              <Link to="/" className="font-medium text-[#c56d33] hover:underline">
                Community
              </Link>
              . Your fellow participants are waiting with motivation, support, and plenty of summer energy.
            </>
          ),
          "Your medal and SWAG will ship in August.",
        ]}
        actions={[{ href: raceData?.metadata?.bib_link, label: "Download BIB" }, communityAction].filter(
          (action) => action.href || action.to
        )}
      />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription={
          <>
            If you purchased gift registrations, you can find the codes below and on your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              account settings page
            </Link>
            .
          </>
        }
        merchandiseText="If you purchased socks, they will ship with your SWAG."
      >
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && <StrengthProgramBlock />}
      </GiftProductsCard>

      <DashboardCtaCard
        description={`Check your ${raceName} dashboard to review your challenge details and get your summer tracking ready.`}
        to={`/dashboard/${dashboardId}`}
      />
    </ConfirmationLayout>
  );
}

function NATURE({ firstName, selectedRace, raceData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);
  const challengeStartDate = selectedRace?.raceInfo?.userStartDate || raceData?.metadata?.userStartDate || "";
  const challengeEndDate = selectedRace?.raceInfo?.userEndDate || raceData?.metadata?.userEndDate || "";
  const challengeWindow =
    challengeStartDate && challengeEndDate
      ? `You have from ${challengeStartDate} to ${challengeEndDate} to complete the challenge and post your kilometres on the platform. Good luck!`
      : "Complete the challenge by posting your kilometres on the platform. Good luck!";
  const trackingDescription = challengeStartDate
    ? `Connect your sports watch or tracking app today so your kilometres start posting automatically on ${challengeStartDate}.`
    : "Connect your sports watch or tracking app today so your kilometres are ready to post when the challenge starts.";
  const communityAction = raceData?.metadata?.fb_link
    ? { href: raceData.metadata.fb_link, label: "Visit Community" }
    : { to: "/", label: "Visit Community" };

  return (
    <ConfirmationLayout
      bannerSrc={raceData?.metadata?.checkoutBanner}
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        `We are looking forward to seeing you answer the call, get outside, and build momentum with ${raceName}.`,
        challengeWindow,
      ]}
      helpEmail="hello@mychallengecanada.ca"
      closingText="Good luck with your challenge. Get out, move more, and enjoy the adventure!"
      signature="The My Challenge Canada Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description={trackingDescription}
        note={
          <>
            You can connect it from your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              Account Settings page
            </Link>
            , or begin posting manually once the challenge starts.
          </>
        }
      />

      <BibCommunityCard
        title="BIB & Community"
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. Download your printable BIB{" "}
            <a href={raceData?.metadata?.bib_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>{" "}
            and share your outdoor adventure on social media.
          </>,
          raceData?.metadata?.fb_link ? (
            <>
              Join the{" "}
              <a href={raceData.metadata.fb_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
                community
              </a>{" "}
              for motivation, support, and plenty of fun.
            </>
          ) : (
            <>
              Create your participant image and introduce yourself in the{" "}
              <Link to="/" className="font-medium text-[#c56d33] hover:underline">
                Community
              </Link>
              . Your fellow participants are ready to cheer you on.
            </>
          ),
        ]}
        actions={[
          { href: raceData?.metadata?.bib_link, label: "Download BIB" },
          communityAction,
          { to: `/dashboard/${dashboardId}`, label: "Create your Image" },
        ].filter((action) => action.href || action.to)}
      />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription={
          <>
            If you purchased gift registrations, you can find the codes below and on your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              account settings page
            </Link>
            .
          </>
        }
      >
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && <StrengthProgramBlock />}
      </GiftProductsCard>

      <DashboardCtaCard
        description={`Check your ${raceName} dashboard to activate automatic distances and create your Nature Calls participant image.`}
        to={`/dashboard/${dashboardId}`}
      />
    </ConfirmationLayout>
  );
}

function SPARK({ firstName, selectedRace, raceData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);
  const challengeStartDate = selectedRace?.raceInfo?.userStartDate || raceData?.metadata?.userStartDate || "";
  const challengeEndDate = selectedRace?.raceInfo?.userEndDate || raceData?.metadata?.userEndDate || "";
  const challengeWindow =
    challengeStartDate && challengeEndDate
      ? `You have from ${challengeStartDate} to ${challengeEndDate} to complete the challenge and post your kilometres on the platform. Good luck!`
      : "Complete the challenge by posting your kilometres on the platform. Good luck!";
  const communityAction = raceData?.metadata?.fb_link
    ? { href: raceData.metadata.fb_link, label: "Visit Community" }
    : { to: "/", label: "Visit Community" };

  return (
    <ConfirmationLayout
      bannerSrc={raceData?.metadata?.checkoutBanner}
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        `We are looking forward to seeing you build connections, stay active, and bring your spark to ${raceName}.`,
        challengeWindow,
      ]}
      helpEmail="hello@mychallengecanada.ca"
      closingText="Good luck with your challenge, and let's keep the social spark going!"
      signature="The My Challenge Canada Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description="Connect your sports watch or tracking app today so your kilometres are ready to post when the challenge starts."
        note={
          <>
            You can connect it from your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              Account Settings page
            </Link>
            , or begin posting manually once the challenge starts.
          </>
        }
      />

      <BibCommunityCard
        title="BIB & Community"
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. Download your printable BIB{" "}
            <a href={raceData?.metadata?.bib_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>{" "}
            and share it on your social media.
          </>,
          raceData?.metadata?.fb_link ? (
            <>
              Join the{" "}
              <a href={raceData.metadata.fb_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
                community
              </a>{" "}
              for motivation, support, and lots of fun.
            </>
          ) : (
            <>
              Don't forget to create your participant image and share it in the{" "}
              <Link to="/" className="font-medium text-[#c56d33] hover:underline">
                Community
              </Link>
              . Your fellow participants are waiting for you with motivation, support, and lots of fun.
            </>
          ),
        ]}
        actions={[{ href: raceData?.metadata?.bib_link, label: "Download BIB" }, communityAction, { to: `/dashboard/${dashboardId}`, label: "Create your Image" }].filter(
          (action) => action.href || action.to
        )}
      />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription={
          <>
            If you purchased gift registrations, you can find the codes below and on your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              account settings page
            </Link>
            .
          </>
        }
      >
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && <StrengthProgramBlock />}
      </GiftProductsCard>

      <DashboardCtaCard
        description={`Check your ${raceName} dashboard to activate automatic distances and create your participant image.`}
        to={`/dashboard/${dashboardId}`}
      />
    </ConfirmationLayout>
  );
}

function RHYTHM({ firstName, selectedRace, raceData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);
  const challengeStartDate = selectedRace?.raceInfo?.userStartDate || raceData?.metadata?.userStartDate || "September 1st, 2026";
  const challengeEndDate = selectedRace?.raceInfo?.userEndDate || raceData?.metadata?.userEndDate || "September 30th, 2026";
  const communityAction = raceData?.metadata?.fb_link
    ? { href: raceData.metadata.fb_link, label: "Visit Community" }
    : { to: "/", label: "Visit Community" };

  return (
    <ConfirmationLayout
      bannerSrc={raceData?.metadata?.checkoutBanner}
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        `We are looking forward to seeing you find your rhythm, move across Canada, and discover its musical landscape with ${raceName}.`,
        `You have from ${challengeStartDate} to ${challengeEndDate} to complete the challenge and post your kilometres on the platform. Good luck!`,
      ]}
      helpEmail="hello@mychallengecanada.ca"
      closingText="Good luck with your challenge. Keep moving, find your rhythm, and enjoy the music along the way!"
      signature="The My Challenge Canada Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description={`Connect your sports watch or tracking app today so your kilometres start posting automatically on ${challengeStartDate}.`}
        note={
          <>
            You can connect it from your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              Account Settings page
            </Link>
            , or begin posting manually once the challenge starts.
          </>
        }
      />

      <BibCommunityCard
        title="BIB & Community"
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. Download your printable BIB{" "}
            <a href={raceData?.metadata?.bib_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>{" "}
            and share how you're finding your rhythm on social media.
          </>,
          raceData?.metadata?.fb_link ? (
            <>
              Join the{" "}
              <a href={raceData.metadata.fb_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
                community
              </a>{" "}
              for motivation, support, musical discoveries, and plenty of fun.
            </>
          ) : (
            <>
              Create your participant image and introduce yourself in the{" "}
              <Link to="/" className="font-medium text-[#c56d33] hover:underline">
                Community
              </Link>
              . Your fellow participants are ready to cheer you on.
            </>
          ),
        ]}
        actions={[
          { href: raceData?.metadata?.bib_link, label: "Download BIB" },
          communityAction,
          { to: `/dashboard/${dashboardId}`, label: "Create your Image" },
        ].filter((action) => action.href || action.to)}
      />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription={
          <>
            If you purchased gift registrations, you can find the codes below and on your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              account settings page
            </Link>
            .
          </>
        }
      >
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && <StrengthProgramBlock />}
      </GiftProductsCard>

      <DashboardCtaCard
        description={`Check your ${raceName} dashboard to activate automatic distances, follow your 13-milestone musical journey, and create your participant image.`}
        to={`/dashboard/${dashboardId}`}
      />
    </ConfirmationLayout>
  );
}

function MILESTONES({ firstName, selectedRace, raceData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);
  const challengeStartDate = selectedRace?.raceInfo?.userStartDate || raceData?.metadata?.userStartDate || "November 1st, 2026";
  const challengeEndDate = selectedRace?.raceInfo?.userEndDate || raceData?.metadata?.userEndDate || "November 30th, 2026";
  const communityAction = raceData?.metadata?.fb_link
    ? { href: raceData.metadata.fb_link, label: "Visit Community" }
    : { to: "/", label: "Visit Community" };

  return (
    <ConfirmationLayout
      bannerSrc={raceData?.metadata?.checkoutBanner}
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        `Keep moving this November and discover Canadian art along the way with ${raceName}.`,
        `You have from ${challengeStartDate} to ${challengeEndDate} to complete the challenge and post your kilometres on the platform. Good luck!`,
      ]}
      helpEmail="hello@mychallengecanada.ca"
      closingText="Enjoy every step this November and the masterpieces you discover along the way!"
      signature="The My Challenge Canada Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description={`Connect your sports watch or tracking app today so your kilometres start posting automatically on ${challengeStartDate}.`}
        note={
          <>
            You can connect it from your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              Account Settings page
            </Link>
            , or begin posting manually once the challenge starts.
          </>
        }
      />

      <BibCommunityCard
        title="BIB & Community"
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. Download your printable BIB{" "}
            <a href={raceData?.metadata?.bib_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>{" "}
            and share your Milestones &amp; Masterpieces journey on social media.
          </>,
          raceData?.metadata?.fb_link ? (
            <>
              Join the{" "}
              <a href={raceData.metadata.fb_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
                community
              </a>{" "}
              for motivation, support, artistic discoveries, and plenty of fun.
            </>
          ) : (
            <>
              Create your participant image and introduce yourself in the{" "}
              <Link to="/" className="font-medium text-[#c56d33] hover:underline">
                Community
              </Link>
              . Your fellow participants are ready to cheer you on.
            </>
          ),
        ]}
        actions={[
          { href: raceData?.metadata?.bib_link, label: "Download BIB" },
          communityAction,
          { to: `/dashboard/${dashboardId}`, label: "Create your Image" },
        ].filter((action) => action.href || action.to)}
      />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription={
          <>
            If you purchased gift registrations, you can find the codes below and on your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              account settings page
            </Link>
            .
          </>
        }
      >
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && <StrengthProgramBlock />}
      </GiftProductsCard>

      <DashboardCtaCard
        description={`Check your ${raceName} dashboard to activate automatic distances, follow your journey through Canadian art, and create your participant image.`}
        to={`/dashboard/${dashboardId}`}
      />
    </ConfirmationLayout>
  );
}

function FALL2026({ firstName, selectedRace, raceData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);
  const challengeStartDate = "October 1st, 2026";
  const challengeEndDate = "October 31st, 2026";
  const communityAction = raceData?.metadata?.fb_link
    ? { href: raceData.metadata.fb_link, label: "Visit Community" }
    : { to: "/", label: "Visit Community" };

  return (
    <ConfirmationLayout
      bannerSrc={raceData?.metadata?.checkoutBanner}
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        `Lace up for crisp air, colourful leaves, and a month of movement with ${raceName}.`,
        `You have from ${challengeStartDate} to ${challengeEndDate} to complete the challenge and post your kilometres on the platform. Good luck!`,
      ]}
      helpEmail="hello@mychallengecanada.ca"
      closingText="Enjoy every step this October. See you out among the fall colours!"
      signature="The My Challenge Canada Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description={`Connect your sports watch or tracking app today so your kilometres start posting automatically on ${challengeStartDate}.`}
        note={
          <>
            You can connect it from your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              Account Settings page
            </Link>
            , or begin posting manually once the challenge starts.
          </>
        }
      />

      <BibCommunityCard
        title="BIB & Community"
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. Download your printable BIB{" "}
            <a href={raceData?.metadata?.bib_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>{" "}
            and share your Joy of Fall Run plans on social media.
          </>,
          raceData?.metadata?.fb_link ? (
            <>
              Join the{" "}
              <a href={raceData.metadata.fb_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
                community
              </a>{" "}
              for motivation, support, and stories from your favourite fall routes.
            </>
          ) : (
            <>
              Create your participant image and introduce yourself in the{" "}
              <Link to="/" className="font-medium text-[#c56d33] hover:underline">
                Community
              </Link>
              . Your fellow participants are ready to cheer you on.
            </>
          ),
        ]}
        actions={[
          { href: raceData?.metadata?.bib_link, label: "Download BIB" },
          communityAction,
          { to: `/dashboard/${dashboardId}`, label: "Create your Image" },
        ].filter((action) => action.href || action.to)}
      />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription={
          <>
            If you purchased gift registrations, you can find the codes below and on your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              account settings page
            </Link>
            .
          </>
        }
      >
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && <StrengthProgramBlock />}
      </GiftProductsCard>

      <DashboardCtaCard
        description={`Check your ${raceName} dashboard to set up automatic tracking, follow your October kilometres, and create your participant image.`}
        to={`/dashboard/${dashboardId}`}
      />
    </ConfirmationLayout>
  );
}

function CHASING({ firstName, selectedRace, raceData }) {
  const raceName = getDisplayRaceName(raceData, selectedRace);
  const dashboardId = getDashboardId(selectedRace);
  const communityAction = raceData?.metadata?.fb_link
    ? { href: raceData.metadata.fb_link, label: "Visit Community" }
    : { to: "/", label: "Visit Community" };

  return (
    <ConfirmationLayout
      bannerSrc={raceData?.metadata?.checkoutBanner}
      bannerAlt={`${raceName} banner`}
      title={getConfirmationTitle(firstName)}
      subtitle={getRegistrationSubtitle(raceName, selectedRace)}
      introParagraphs={[
        `We are looking forward to seeing you build momentum this spring, stay active, and have fun with ${raceName}.`,
        "You have from May 1st, 2026 to May 31st, 2026 to complete the challenge and post your kilometres on the platform. Good luck!",
      ]}
      helpEmail="hello@mychallengecanada.ca"
      closingText="Good luck with your challenge, and let's stay active, healthy, and consistent!"
      signature="The My Challenge Canada Team"
    >
      <ConfirmationSectionTitle>Your Next Steps</ConfirmationSectionTitle>

      <DeviceTrackingCard
        description="Connect your sports watch or tracking app today so your kilometres start posting automatically on May 1st."
        note={
          <>
            You can connect it from your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              Account Settings page
            </Link>
            , or begin posting manually once the challenge starts.
          </>
        }
      />

      <BibCommunityCard
        items={[
          <>
            Your BIB number is <strong>{selectedRace?.bibNumber}</strong>. Download your printable BIB{" "}
            <a href={raceData?.metadata?.bib_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
              here
            </a>{" "}
            and share it on your social media.
          </>,
          raceData?.metadata?.fb_link ? (
            <>
              Join the{" "}
              <a href={raceData.metadata.fb_link} target="_blank" rel="noreferrer" className="font-medium text-[#c56d33] hover:underline">
                community
              </a>{" "}
              for motivation, support, and lots of fun.
            </>
          ) : (
            <>
              Don't forget to introduce yourself in the{" "}
              <Link to="/" className="font-medium text-[#c56d33] hover:underline">
                Community
              </Link>
              . Your fellow participants are waiting for you with motivation, support, and lots of fun.
            </>
          ),
        ]}
        actions={[{ href: raceData?.metadata?.bib_link, label: "Download BIB" }, communityAction].filter(
          (action) => action.href || action.to
        )}
      />

      <GiftProductsCard
        selectedRace={selectedRace}
        giftDescription={
          <>
            If you purchased gift registrations, you can find the codes below and on your{" "}
            <Link to="/account" className="font-medium text-[#c56d33] hover:underline">
              account settings page
            </Link>
            .
          </>
        }
      >
        {selectedRace?.boughtProducts &&
          Object.values(selectedRace.boughtProducts).includes("4-Week Stronger Runner Challenge") && <StrengthProgramBlock />}
      </GiftProductsCard>

      <DashboardCtaCard
        description={`Check your ${raceName} dashboard to activate automatic distances and create your participant image.`}
        to={`/dashboard/${dashboardId}`}
      />
    </ConfirmationLayout>
  );
}
